import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { API, PlayoutDTO } from 'src/app/services/api/api';
import { SbDateTime } from 'src/app/services/api/api-date-time';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from 'src/app/services/util.service';

@Component({
  selector: 'sb-comp-device-playouts',
  templateUrl: './sb-device-playouts.component.html',
  styleUrls: ['./sb-device-playouts.component.scss'],
  standalone: false,
})
export class SbDevicePlayoutsComponent extends DataSource<PlayoutDTO | undefined> implements OnInit, OnChanges {
  @Input() deviceId: number;
  @Input() date: SbDateTime;

  placeholderHeight = 0;
  displayedColumns: string[] = ['descriptor', 'motive', 'start', 'playbacktime'];

  private cachedPlayouts = Array.from<PlayoutDTO>({ length: 0 });
  private dataStream = new BehaviorSubject<(PlayoutDTO | undefined)[]>(this.cachedPlayouts);
  private subscription = new Subscription();

  private pageSize = 10;
  private lastPage = 0;

  loading: boolean = true;

  requesting: boolean = false;

  constructor(
    private api: API,
    public util: UtilService,
    public errorHandler: ErrorService
  ) {
    super();
  }

  ngOnInit(): void {
    this._fetchPage(null);
  }

  placeholderWhen(index: number, _: any) {
    return index === 0;
  }

  clear() {
    this.lastPage = 0;
    this.cachedPlayouts = Array.from<PlayoutDTO>({ length: 0 });
    this.dataStream.next(this.cachedPlayouts);
  }

  connect(collectionViewer: CollectionViewer): Observable<(PlayoutDTO | undefined)[] | ReadonlyArray<PlayoutDTO | undefined>> {
    return this.dataStream;
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.subscription.unsubscribe();
  }

  private _fetchPage(rangeEnd: number): void {
    if (!this.requesting) {
      this.requesting = true;
      let after: number;
      let afterTS: SbDateTime;

      if (rangeEnd && this.cachedPlayouts && this.cachedPlayouts.length > 0) {
        const playout: PlayoutDTO = this.cachedPlayouts[this.cachedPlayouts.length - 1];
        after = playout.descriptor;
        afterTS = playout.start;
      } else {
        after = null;
      }
      this.api
        .ss_nm_Device_fetchPlayouts(this.deviceId, {
          after_timestamp: afterTS,
          after_descriptor: after,
          count: this.pageSize,
          till: this.date,
        })
        .subscribe({
          next: (res: PlayoutDTO[]) => {
            if (this.displayedColumns.indexOf('templateDataset') === -1) {
              // check if any Playout DTO has templateEntry
              const hasTemplateEntry = res.some(p => !!p?.usedTemplateEntry?.name);
              if (hasTemplateEntry) {
                // show templateDataset column
                this.displayedColumns.splice(this.displayedColumns.indexOf('motive') + 1, 0, 'templateDataset');
              }
            }

            this.cachedPlayouts = this.cachedPlayouts.concat(res);
            this.dataStream.next(this.cachedPlayouts);
            if (!res || !res.length || res.length === 0 || res.length < this.pageSize) {
              this.loading = false;
            }
            this.requesting = false;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
            this.requesting = false;
          },
        });
    }
  }

  private _getPageForIndex(i: number): number {
    return Math.floor(i / this.pageSize);
  }

  onTableScroll(e) {
    const tableViewHeight = e.target.offsetHeight; // viewport: ~500px
    const tableScrollHeight = e.target.scrollHeight; // length of all table
    const scrollLocation = e.target.scrollTop; // how far user scrolled

    // If the user has scrolled within 200px of the bottom, add more data
    const buffer = 1;
    const limit = tableScrollHeight - tableViewHeight - buffer;
    if (scrollLocation > limit) {
      this.loading = true;
      this._fetchPage(1337);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.date) {
      this.date = changes.date.currentValue;
      this.cachedPlayouts = [];
      this._fetchPage(null);
    }
  }
}
